/**
 * Google Analytics 4 configuration.
 *
 * The Measurement ID is a single value shared across dev/uat/prod — environments
 * are segmented inside GA4 via the `environment` custom dimension, which is
 * derived from the browser hostname at runtime (not a build-time env var).
 * Hostname is the authoritative boundary and self-corrects if an image is ever
 * deployed to the wrong Cloud Run service.
 */

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

export const IS_GA_ENABLED = Boolean(GA_MEASUREMENT_ID);

export type AppEnv = 'prod' | 'uat' | 'dev' | 'local';

/**
 * Determine the current environment from the browser hostname. Runs only in the
 * browser; server-rendered code should not call this (returns 'prod' as a safe
 * default if called on the server).
 */
export function detectEnv(): AppEnv {
  if (typeof window === 'undefined') return 'prod';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host.startsWith('dev.')) return 'dev';
  if (host.startsWith('uat.')) return 'uat';
  return 'prod';
}

/**
 * Hostname gate shared by every analytics vendor integration: local
 * development never loads analytics, even when the env vars are present.
 * Hostname (not NODE_ENV) is the authoritative boundary — it self-corrects
 * if an image is ever run somewhere unexpected.
 */
export function isTrackableHost(host: string): boolean {
  if (host === 'localhost' || host === '127.0.0.1') return false;
  if (host.endsWith('.local')) return false;
  return true;
}
